import { useEffect, useRef, useState } from "react";

import { allTrackers } from "app/services/track/track";

interface UseDraggableSideTabOptions {
    trackingEvent: string;
    verticalOffsetAdjustment?: number;
    onClick?: () => void;
}

export const useDraggableSideTab = (options: UseDraggableSideTabOptions) => {
    const { trackingEvent, verticalOffsetAdjustment = 0, onClick } = options;

    const wrapperRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef<boolean>(false);
    const [dragState, setDragState] = useState<boolean>(false);
    const [horizontalOffset, setHorizontalOffset] = useState<number>(0);
    const [verticalOffsetDelta, setVerticalOffsetDelta] = useState<number>(0);

    const calcOffset = () => {
        const parent = wrapperRef?.current?.parentElement;
        if (!parent) {
            setHorizontalOffset(0);
            return;
        }

        try {
            const parentBounds = parent.getBoundingClientRect()?.right ?? 0;
            const rectOffset = window.innerWidth - parentBounds;
            const scrollbarOffset = parent.offsetWidth - parent.clientWidth;
            setHorizontalOffset(Math.max(rectOffset, scrollbarOffset));
        } catch (error) {
            setHorizontalOffset(0);
        }
    };

    const trackEvent = (): void => {
        allTrackers.dispatchTrackEvent("button", "click", trackingEvent);
        onClick?.();
    };

    const onMouseDown = (): void => {
        document.addEventListener("mousemove", dragTab);
        document.addEventListener("mouseup", onMouseUp);
    };

    const onMouseUp = (): void => {
        if (!isDragging.current) {
            trackEvent();
        } else {
            isDragging.current = false;
            setDragState(false);
        }
        document.removeEventListener("mousemove", dragTab);
        document.removeEventListener("mouseup", onMouseUp);
    };

    const dragTab = (ev: MouseEvent): void => {
        const delta = -ev.movementY;
        setVerticalOffsetDelta((prev) => prev + delta);
        setDragState(true);
        isDragging.current = true;
    };

    useEffect(() => {
        if (!wrapperRef.current) {
            return;
        }

        const timer = setTimeout(() => {
            calcOffset();
        }, 0);

        const resizeObserver = new ResizeObserver(calcOffset);
        if (wrapperRef?.current?.parentElement) {
            resizeObserver.observe(wrapperRef.current.parentElement);
        }

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
            document.removeEventListener("mousemove", dragTab);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, []);

    return {
        wrapperRef,
        dragState,
        horizontalOffset,
        verticalOffsetDelta: verticalOffsetDelta + verticalOffsetAdjustment,
        handleMouseDown: onMouseDown,
    };
};
